import { LANE_INDEXES, TABLE_IDS, cellId, opposing, parseCellId, } from '@cards/engine';
/** Read one committed unit selection without depending on legacy scalar plan fields. */
export function committedUnitTarget(ctx, stepId = 'target') {
    const plan = ctx.plan;
    if (plan === undefined)
        return undefined;
    const selected = plan.selections
        ?.find((step) => step.stepId === stepId)
        ?.selection.find((candidate) => candidate.kind === 'unit');
    if (selected?.kind === 'unit')
        return selected.instanceId;
    return 'targetInstanceId' in plan ? plan.targetInstanceId : undefined;
}
/** A field card is live only while it still occupies its authoritative board cell. */
export function liveFieldCard(ctx, instanceId) {
    const instance = ctx.q.instance(instanceId);
    if (instance === null || instance.zone !== 'board' || instance.cellId === undefined)
        return undefined;
    return ctx.q.cell(instance.cellId)?.occupantId === instance.id ? instance : undefined;
}
export function liveAlliedFieldCard(ctx, instanceId, controllerId) {
    const instance = liveFieldCard(ctx, instanceId);
    return instance?.controllerId === controllerId ? instance : undefined;
}
export function liveEnemyFieldCard(ctx, instanceId, controllerId) {
    const instance = liveFieldCard(ctx, instanceId);
    return instance !== undefined && instance.controllerId !== controllerId ? instance : undefined;
}
/** All three cells on the controller's side of the anchor location are occupied by that controller. */
export function isLocationFullyFurnished(ctx, controllerId, anchorCellId) {
    const tableId = parseCellId(anchorCellId).tableId;
    const side = ctx.q.player(controllerId).side;
    return LANE_INDEXES.every((laneIndex) => {
        const occupant = ctx.q.unitAt(cellId(tableId, laneIndex, side));
        return occupant !== null && occupant.controllerId === controllerId;
    });
}
/**
 * True when every currently existing cell on the anchor side of a location is
 * occupied. Unlike furniture's full-room condition, card controller and type
 * do not matter. Missing cells are deliberately ignored, so a future board
 * that removes a cell needs only its remaining cells filled.
 */
export function isLocationFilled(ctx, anchorCellId) {
    const { tableId, side } = parseCellId(anchorCellId);
    const cells = LANE_INDEXES
        .map((laneIndex) => ctx.q.cell(cellId(tableId, laneIndex, side)))
        .filter((cell) => cell !== null);
    return cells.length > 0 && cells.every((cell) => cell.occupantId !== null);
}
/** Resolve the current live enemy occupant opposite the remembered source cell. */
export function liveOpposingEnemy(ctx, sourceCellId, controllerId) {
    const occupant = ctx.q.unitAt(opposing(sourceCellId));
    if (occupant === null || occupant.controllerId === controllerId)
        return undefined;
    return liveFieldCard(ctx, occupant.id);
}
/** Death furniture attributes rewards to the last controller for any Гибель. */
export function deathController(ctx, event) {
    return event.controllerIdAtDeath ?? ctx.self?.controllerId;
}
/** Pure committed-cost predicate: staged placements are intentionally absent from the board scan. */
export function hasFieldCardInEveryLocation(state, controllerId) {
    return TABLE_IDS.every((tableId) => Object.values(state.board.cells).some((cell) => {
        if (cell.tableId !== tableId || cell.occupantId === null)
            return false;
        const occupant = state.instances[cell.occupantId];
        return occupant !== undefined
            && occupant.zone === 'board'
            && occupant.cellId === cell.id
            && occupant.controllerId === controllerId;
    }));
}
const ATTACK_AURA_TARGETS_PREFIX = '@cards/furniture/attack_aura/';
const ATTACK_AURA_ABILITY_VAR = 'furnitureAttackAuraAbility';
function instanceIds(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((candidate) => typeof candidate === 'string');
}
function sameIds(left, right) {
    return left.length === right.length && left.every((id, index) => id === right[index]);
}
/**
 * Keep one source-bound attack effect per desired live target.
 * The source records only canonical target ids; each effect carries the stable
 * ability id, so replay/hydration never depends on module state or file order.
 */
export function reconcileSourceBoundAttackAura(ctx, input) {
    const targetKey = `${ATTACK_AURA_TARGETS_PREFIX}${input.abilityId}/targets`;
    const previousTargetIds = instanceIds(input.source.vars[targetKey]);
    const desiredTargets = input.targets.flatMap((candidate) => {
        const live = liveFieldCard(ctx, candidate.id);
        return live === undefined ? [] : [live];
    });
    const desiredTargetIds = [...new Set(desiredTargets.map(({ id }) => id))];
    const desired = new Set(desiredTargetIds);
    const candidates = new Set([
        ...previousTargetIds,
        ...desiredTargetIds,
        ...ctx.q.allUnits().map(({ id }) => id),
    ]);
    for (const targetId of candidates) {
        const matching = ctx.q.effectsOn({ kind: 'unit', instanceId: targetId })
            .filter((effect) => effect.defId === input.effectDefId
            && effect.sourceId === input.source.id
            && effect.vars[ATTACK_AURA_ABILITY_VAR] === input.abilityId);
        if (!desired.has(targetId)) {
            for (const effect of matching)
                ctx.ops.removeEffect(effect.id);
            continue;
        }
        if (matching.length === 0) {
            ctx.ops.addEffect({
                defId: input.effectDefId,
                scope: { kind: 'unit', instanceId: targetId },
                sourceId: input.source.id,
                vars: { [ATTACK_AURA_ABILITY_VAR]: input.abilityId },
                statMods: [{ stat: 'attack', op: 'add', value: input.attack }],
            });
            continue;
        }
        for (const duplicate of matching.slice(1))
            ctx.ops.removeEffect(duplicate.id);
    }
    if (!sameIds(previousTargetIds, desiredTargetIds)) {
        ctx.ops.setVar(targetKey, desiredTargetIds);
    }
}
/** Remove every reconciled effect when the source leaves play. */
export function clearSourceBoundAttackAura(ctx, input) {
    reconcileSourceBoundAttackAura(ctx, { ...input, targets: [] });
}
/** Effect-owned cleanup for a source that stays on board under a new definition. */
export function removeAttackAuraAfterSourceTransform(ctx, event, abilityId) {
    const effect = ctx.effect;
    if (effect === undefined
        || event.type !== 'CardTransformed'
        || event.instanceId !== effect.sourceId
        || effect.vars[ATTACK_AURA_ABILITY_VAR] !== abilityId)
        return false;
    ctx.ops.removeEffect(effect.id);
    return true;
}
//# sourceMappingURL=furniture_helpers.js.map